import React from 'react'
import i18n from 'i18next'
import { Notification, toast } from 'components/ui'
import { API_HOST } from './http'

/**
 * Build the backend OAuth-connect URL for a social network.
 *
 * Returns `null` when `companyId` (or `network`) is missing instead of
 * interpolating the literal string "undefined" into the path — which is what
 * produced `GET /companies/undefined/socialNetworks/...` and crashed the API.
 *
 * The token stays a query param (this is a full-page/popup navigation to the
 * backend, not an axios XHR, so it cannot use the Authorization header).
 *
 * @param {number|string} companyId
 * @param {string} network
 * @param {string} frontendUrl
 * @returns {string|null}
 */
export function buildSocialNetworkConnectUrl(companyId, network, frontendUrl) {
    if (!companyId || !network) return null

    let token = null
    try {
        token = localStorage.getItem('token')
    } catch (_) {
        /* localStorage unavailable */
    }

    return (
        `${API_HOST}/companies/${companyId}/socialNetworks/${network}` +
        `?token=${token}&frontend_url=${frontendUrl}`
    )
}

function notifyMissingCompany() {
    toast.push(
        <Notification
            type="warning"
            title={i18n.t('socialNetworkConnect.selectCompanyFirst')}
        />
    )
}

/**
 * Open the OAuth-connect flow in a new window.
 * Returns `false` (and shows a "select a company" toast) when no company is
 * available, so callers can bail out before flipping loading state.
 */
export function openSocialNetworkConnect(companyId, network, frontendUrl) {
    const url = buildSocialNetworkConnectUrl(companyId, network, frontendUrl)
    if (!url) {
        notifyMissingCompany()
        return false
    }
    window.open(url)
    return true
}

/**
 * Same as {@link openSocialNetworkConnect} but navigates the current window.
 * Returns `false` (and toasts) when no company is available.
 */
export function redirectToSocialNetworkConnect(companyId, network, frontendUrl) {
    const url = buildSocialNetworkConnectUrl(companyId, network, frontendUrl)
    if (!url) {
        notifyMissingCompany()
        return false
    }
    window.location.href = url
    return true
}
